import { useContext } from 'react'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import { useNls } from '../../nls/nlsProvider'
import { Button } from '../Common'
import { ItemTimeContext, ItemTimeProvider, useItemTime } from './ItemTimeProvider'
import './timesheets.css'

export interface ItemTimerButtonProps {
  workspaceId?: string
  nodeId?: string | null
  nodeLabel?: string | null
  className?: string
  size?: 'sm' | 'md'
  variant?: 'primary' | 'secondary' | 'tertiary' | 'link' | 'danger'
  testId?: string
}

export const ItemTimerButton = (props: ItemTimerButtonProps) => {
  const context = useContext(ItemTimeContext)
  if (!context) {
    return (
      <ItemTimeProvider workspaceId={props.workspaceId}>
        <ItemTimerButtonContent {...props} />
      </ItemTimeProvider>
    )
  }
  return <ItemTimerButtonContent {...props} />
}

const ItemTimerButtonContent = ({
  nodeId,
  nodeLabel,
  className = '',
  size = 'sm',
  variant,
  testId = 'item-timer-btn',
}: ItemTimerButtonProps) => {
  const context = useContext(ItemTimeContext)
  const { t } = useNls()
  const { isRunningHere, elapsedText, start, stop, busy } = useItemTime(nodeId)

  if (!context?.isEnabled || !nodeId || nodeId.startsWith('draft-')) {
    return null
  }

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (isRunningHere) {
      await stop()
    } else {
      await start()
    }
  }

  const buttonLabel = isRunningHere
    ? t('NLSTimesheetsItemStopTimerElapsed', { elapsed: elapsedText })
    : t('NLSTimesheetsItemStartTimer')

  const resolvedVariant = variant ?? (isRunningHere ? 'secondary' : 'tertiary')

  return (
    <Button
      variant={resolvedVariant}
      size={size}
      className={`itemTimerButton ${isRunningHere ? 'itemTimerButton--running' : ''} ${className}`.trim()}
      onClick={handleClick}
      disabled={busy}
      data-testid={testId}
      aria-label={nodeLabel ? `${buttonLabel} (${nodeLabel})` : buttonLabel}
    >
      {isRunningHere ? (
        <StopRoundedIcon fontSize="inherit" aria-hidden="true" />
      ) : (
        <PlayArrowRoundedIcon fontSize="inherit" aria-hidden="true" />
      )}
      <span>{buttonLabel}</span>
    </Button>
  )
}
